/**
 * Custom hook for managing Wealthica position state and loading
 * Consolidates all position-related state and logic from Wealthica2GoldContainer
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { logger } from '@/services/LoggingService';
import { AccountWithPositions } from '../components/ui/container-utils';
import { AccountPositionsMap, organizePositionsByAccount } from '../utils/account-position-utils';
import { getCachedPositionsArray } from '../utils/cache-utils';
import type { WealthicaAccount } from '../types';
import { useMultiWealthica2Store } from '@/stores/multiWealthica2Store';

interface UseWealthicaPositionsOptions {
  wealthicaAccounts: WealthicaAccount[];
  connectedInstitutionId?: string;
  persistDataPreference: boolean;
  // When true, the hook will auto-fetch positions once when accounts first appear.
  // Default is false to avoid duplicate fetches; the store coordinates fetching.
  autoFetchInitial?: boolean;
}

interface UseWealthicaPositionsReturn {
  accountsWithPositions: AccountWithPositions[];
  organizedPositionsMap: AccountPositionsMap;
  expandedAccounts: string[];
  loadingPositionsFor: Record<string, boolean>;
  isLoadingPositions: boolean;
  loadAllPositionsForAccounts: () => Promise<void>;
  loadPositionsForAccount: (accountId: string) => Promise<void>;
  toggleAccountExpansion: (account: any) => void;
  setExpandedAccountsList: (next: string[]) => void;
  hasLoadedInitialPositions: boolean;
}

export function useWealthicaPositions({
  wealthicaAccounts,
  connectedInstitutionId,
  persistDataPreference,
  autoFetchInitial = false
}: UseWealthicaPositionsOptions): UseWealthicaPositionsReturn {
  // Position state
  const [accountsWithPositions, setAccountsWithPositions] = useState<AccountWithPositions[]>([]);
  const [organizedPositionsMap, setOrganizedPositionsMap] = useState<AccountPositionsMap>({});
  const [expandedAccounts, setExpandedAccounts] = useState<string[]>([]);
  const [loadingPositionsFor, setLoadingPositionsFor] = useState<Record<string, boolean>>({});
  const [isLoadingPositions, setIsLoadingPositions] = useState<boolean>(false);
  const didInitialPositionsLoadRef = useRef<boolean>(false);
  // Stability guards
  const lastAwpKeyRef = useRef<string>("");
  const lastMapKeyRef = useRef<string>("");
  const computeAwpKey = (list: AccountWithPositions[]) =>
    (list || [])
      .map((a) => `${a.id}:${a.positions?.length || 0}`)
      .join("|");
  const computeMapKey = (map: AccountPositionsMap) => {
    const keys = Object.keys(map || {}).sort();
    const parts: string[] = [String(keys.length)];
    keys.forEach((k) => {
      const v: any = (map as any)[k];
      if (Array.isArray(v)) {
        parts.push(`${k}:${v.length}`);
      } else if (v && typeof v === 'object' && Array.isArray((v as any).positions)) {
        parts.push(`${k}:${(v as any).positions.length}`);
      } else {
        parts.push(`${k}:obj`);
      }
    });
    return parts.join("|");
  };
  
  // Request deduplication for position loading
  const activeRequestRef = useRef<Promise<void> | null>(null);
  
  // Batch load all positions with deduplication
  const loadAllPositionsForAccounts = useCallback(async (): Promise<void> => {
    // Deduplication check
    if (activeRequestRef.current) {
      logger.info('useWealthicaPositions.loadAllPositions', 'Deduplicating request - waiting for existing load');
      return activeRequestRef.current;
    }

    if (!wealthicaAccounts || (wealthicaAccounts?.length || 0) === 0) {
      logger.warn('useWealthicaPositions.loadAllPositions', 'No accounts to load positions for');
      return;
    }
    // Build accounts per institution for selective loading
    const accountsByInstitution: Record<string, any[]> = {};
    (wealthicaAccounts || []).forEach((acc: any) => {
      const instId = acc?.institutionId;
      if (instId) {
        (accountsByInstitution[instId] ||= []).push(acc);
      }
    });

    // Decide which institutions actually need a positions fetch
    // Rule: skip if ALL accounts for that institution are cash-only AND there are no explicit positions hints
    const storeNow = useMultiWealthica2Store.getState();
    const idsToLoad = Object.keys(accountsByInstitution).filter((instId) => {
      const list = accountsByInstitution[instId] || [];
      const anyPositionsHint = list.some((a: any) => Array.isArray(a?.positions) && (a.positions?.length || 0) > 0);
      const allCash = list.every((a: any) => (a?.classification?.cdn_asset_class || '').toLowerCase() === 'cash');
      // Fetch positions if we have any hint, or if not all are cash (they may have holdings)
      // Always fetch at least once per institution even if accounts look cash-only
      const hasFetchedBefore = !!(storeNow.connections?.get?.(instId) as any)?.lastPositionsFetch;
      return anyPositionsHint || !allCash || !hasFetchedBefore;
    });

    if (idsToLoad.length === 0) {
      logger.info('useWealthicaPositions.loadAllPositions', 'Skipping positions fetch - accounts indicate no holdings', {
        accountCount: wealthicaAccounts.length,
        institutionsSkipped: Object.keys(accountsByInstitution).length
      });
      // Ensure UI is not stuck in loading state and mark initial pass complete
      setIsLoadingPositions(false);
      didInitialPositionsLoadRef.current = true;
      return;
    }

    logger.info('useWealthicaPositions.loadAllPositions', 'Starting position loading', {
      accountCount: wealthicaAccounts.length,
      institutionId: connectedInstitutionId,
      institutionsToLoad: idsToLoad,
      note: 'Multi-institution aggregation active'
    });
    
    // Signal UI that positions are loading only when we actually plan to fetch
    setIsLoadingPositions((prev) => (prev ? prev : true));
    
    // Create promise and store reference
    const requestPromise = (async () => {
      try {
        // Use centralized multi-store position fetching
        const { useMultiWealthica2Store } = await import('../../../stores/multiWealthica2Store');
        const { fetchPositionsForInstitution } = useMultiWealthica2Store.getState();

        // Use the filtered institutions list computed above

        // Fetch positions for each institution sequentially to avoid API bursts
        for (const instId of idsToLoad) {
          try {
            await fetchPositionsForInstitution(instId);
          } catch (e) {
            logger.warn('useWealthicaPositions.loadAllPositions', 'Failed to fetch positions for institution', {
              institutionId: instId,
              error: e instanceof Error ? e.message : String(e)
            });
          }
        }

        // Aggregate positions from store across all loaded institutions
        const store = useMultiWealthica2Store.getState();
        const rawPositionsAll: any[] = [];
        idsToLoad.forEach(instId => {
          const conn = store.connections.get(instId);
          if (conn?.positions?.length) {
            rawPositionsAll.push(...conn.positions);
          }
        });

        // Build accountsInfo map from all available accounts
        const accountsInfo: Record<string, string> = {};
        (wealthicaAccounts || []).forEach(acc => { accountsInfo[acc.id] = acc.name || acc.id; });

        // Organize positions by account using our official mapping utility
        const { accountMap, positionsMap } = organizePositionsByAccount(rawPositionsAll as any[], accountsInfo);

        // Convert accountMap to the UI-friendly accountsWithPositions array
        const newAccountsWithPositions = Object.keys(accountMap).map(accId => ({
          id: accId,
          positions: accountMap[accId]
        }));

        // Update local state with aggregated positions across institutions
        // Only update when content actually changed
        const newAwpKey = computeAwpKey(newAccountsWithPositions);
        const newMapKey = computeMapKey(positionsMap as AccountPositionsMap);
        if (newAwpKey !== lastAwpKeyRef.current) {
          setAccountsWithPositions(newAccountsWithPositions);
          lastAwpKeyRef.current = newAwpKey;
        }
        if (newMapKey !== lastMapKeyRef.current) {
          setOrganizedPositionsMap(positionsMap as AccountPositionsMap);
          lastMapKeyRef.current = newMapKey;
        }
        setIsLoadingPositions((prev) => (prev ? false : prev));
        
        // Mark initial load and remember which institutions are loaded
        didInitialPositionsLoadRef.current = true;
      
        logger.info('useWealthicaPositions.loadAllPositions', 'Positions loaded successfully', {
          accountsWithPositionsCount: newAccountsWithPositions.length,
          organizedMapSize: Object.keys(positionsMap).length,
          institutionsLoaded: idsToLoad.length
        });
      } catch (error) {
        logger.error('useWealthicaPositions.loadAllPositions', 'Error loading positions', { error });
        toast.error('Error loading investment positions');
        throw error;
      } finally {
        // Clear reference when done
        activeRequestRef.current = null;
      }
    })();

    activeRequestRef.current = requestPromise;
    return requestPromise;
  }, [wealthicaAccounts, connectedInstitutionId, persistDataPreference]);

  // Subscribe to multi-store position updates to hydrate UI without re-fetching
  // This ensures that when positions are fetched by the store (e.g., via container
  // refresh or store auto-fetch), we still display them even if this hook's initial
  // auto-fetch was skipped (e.g., due to only cash accounts being present initially).
  useEffect(() => {
    const rebuildFromStore = (state?: any) => {
      try {
        const st = state || useMultiWealthica2Store.getState();
        if (!wealthicaAccounts || wealthicaAccounts.length === 0) return;

        const institutionIds = Array.from(new Set((wealthicaAccounts || [])
          .map((a: any) => a?.institutionId)
          .filter((id): id is string => !!id)));

        const rawPositionsAll: any[] = [];
        institutionIds.forEach((id) => {
          const conn = st.connections?.get?.(id);
          if (conn?.positions?.length) rawPositionsAll.push(...conn.positions);
        });

        if (rawPositionsAll.length === 0) {
          // Avoid clearing previously loaded positions during transitions
          return;
        }

        const accountsInfo: Record<string, string> = {};
        (wealthicaAccounts || []).forEach(acc => { accountsInfo[acc.id] = acc.name || acc.id; });
        const { accountMap, positionsMap } = organizePositionsByAccount(rawPositionsAll as any[], accountsInfo);
        const newAccountsWithPositions = Object.keys(accountMap).map(accId => ({ id: accId, positions: accountMap[accId] }));

        const newAwpKey = computeAwpKey(newAccountsWithPositions);
        const newMapKey = computeMapKey(positionsMap as AccountPositionsMap);
        if (newAwpKey !== lastAwpKeyRef.current) {
          setAccountsWithPositions(newAccountsWithPositions);
          lastAwpKeyRef.current = newAwpKey;
        }
        if (newMapKey !== lastMapKeyRef.current) {
          setOrganizedPositionsMap(positionsMap as AccountPositionsMap);
          lastMapKeyRef.current = newMapKey;
        }
      } catch (e) {
        logger.warn('useWealthicaPositions', 'Subscription aggregation failed', { error: e });
      }
    };

    // Initial hydration in case positions already exist in the store
    rebuildFromStore();

    // Subscribe to store updates
    const unsubscribe = useMultiWealthica2Store.subscribe((state) => {
      rebuildFromStore(state);
    });

    return () => {
      try { unsubscribe?.(); } catch {}
    };
  }, [wealthicaAccounts?.length]);

  // Load positions for a single account
  const loadPositionsForAccount = useCallback(async (accountId: string): Promise<void> => {
    if (!accountId) {
      logger.error('useWealthicaPositions.loadPositionsForAccount', 'No account ID provided');
      return;
    }
    
    setLoadingPositionsFor(prev => (prev[accountId] === true ? prev : { ...prev, [accountId]: true }));
    
    try {
      const accountToLoad = wealthicaAccounts.find(acc => acc.id === accountId);
      
      if (!accountToLoad) {
        logger.error('useWealthicaPositions.loadPositionsForAccount', `Account ${accountId} not found`);
        return;
      }
      
      // Fast-path skip: if all accounts for this account's institution are cash-only with no
      // positions hints, avoid the expensive positions call entirely.
      const instIdForAccount = (accountToLoad as any).institutionId || connectedInstitutionId;
      if (instIdForAccount) {
        const accountsForInst = (wealthicaAccounts || []).filter((a: any) => a?.institutionId === instIdForAccount);
        const anyPositionsHint = accountsForInst.some((a: any) => Array.isArray(a?.positions) && (a.positions?.length || 0) > 0);
        const allCash = accountsForInst.length > 0 && accountsForInst.every((a: any) => (a?.classification?.cdn_asset_class || '').toLowerCase() === 'cash');
        if (!anyPositionsHint && allCash) {
          logger.info('useWealthicaPositions.loadPositionsForAccount', 'Skipping positions fetch - institution appears cash-only', {
            institutionId: instIdForAccount,
            accounts: accountsForInst.length
          });
          return;
        }
      }

      // Use centralized multi-store position fetching for single account
      const { useMultiWealthica2Store } = await import('../../../stores/multiWealthica2Store');
      const { fetchPositionsForInstitution } = useMultiWealthica2Store.getState();
      
      const instId = (accountToLoad as any).institutionId || connectedInstitutionId;
      if (instId) {
        await fetchPositionsForInstitution(instId);
      }

      // After fetch, aggregate positions across all institutions to preserve previously loaded data
      const store = useMultiWealthica2Store.getState();
      const institutionIds = Array.from(new Set((wealthicaAccounts || [])
        .map(acc => (acc as any).institutionId)
        .filter((id): id is string => !!id)));
      const rawPositionsAll: any[] = [];
      institutionIds.forEach(id => {
        const conn = store.connections.get(id);
        if (conn?.positions?.length) rawPositionsAll.push(...conn.positions);
      });

      const accountsInfo: Record<string, string> = {};
      (wealthicaAccounts || []).forEach(acc => { accountsInfo[acc.id] = acc.name || acc.id; });
      const { accountMap, positionsMap } = organizePositionsByAccount(rawPositionsAll as any[], accountsInfo);
      const newAccountsWithPositions = Object.keys(accountMap).map(accId => ({ id: accId, positions: accountMap[accId] }));
      const newAwpKey = computeAwpKey(newAccountsWithPositions);
      if (newAwpKey !== lastAwpKeyRef.current) {
        setAccountsWithPositions(newAccountsWithPositions);
        lastAwpKeyRef.current = newAwpKey;
      }
      const newMapKey = computeMapKey(positionsMap);
      if (newMapKey !== lastMapKeyRef.current) {
        setOrganizedPositionsMap(positionsMap);
        lastMapKeyRef.current = newMapKey;
      }
    } catch (error) {
      logger.error('useWealthicaPositions.loadPositionsForAccount', `Error loading positions for account ${accountId}`, { error });
      toast.error('Failed to load positions for this account.');
    } finally {
      setLoadingPositionsFor(prev => (prev[accountId] === false ? prev : { ...prev, [accountId]: false }));
    }
  }, [wealthicaAccounts, connectedInstitutionId, persistDataPreference]);

  // Initialize positions flag based on cached data
  useEffect(() => {
    if (connectedInstitutionId && !didInitialPositionsLoadRef.current) {
      const cachedPositions = getCachedPositionsArray(connectedInstitutionId);
      if (cachedPositions && cachedPositions.length > 0) {
        logger.info('useWealthicaPositions', 'Found cached positions, hydrating UI state immediately', {
          cachedPositionCount: cachedPositions.length,
          institutionId: connectedInstitutionId
        });

        // Build accountsInfo map from available accounts for proper display names
        const accountsInfo: Record<string, string> = {};
        (wealthicaAccounts || []).forEach(acc => { accountsInfo[acc.id] = acc.name || acc.id; });

        // Organize cached positions so UI renders without waiting for a fetch
        const { accountMap, positionsMap } = organizePositionsByAccount(cachedPositions as any[], accountsInfo);

        const hydratedAccountsWithPositions = Object.keys(accountMap).map(accId => ({
          id: accId,
          positions: accountMap[accId]
        }));

        const newAwpKey = computeAwpKey(hydratedAccountsWithPositions);
        if (newAwpKey !== lastAwpKeyRef.current) {
          setAccountsWithPositions(hydratedAccountsWithPositions);
          lastAwpKeyRef.current = newAwpKey;
        }
        const newMapKey = computeMapKey(positionsMap);
        if (newMapKey !== lastMapKeyRef.current) {
          setOrganizedPositionsMap(positionsMap);
          lastMapKeyRef.current = newMapKey;
        }

        // Mark initial load complete so we don't refetch immediately due to this effect
        didInitialPositionsLoadRef.current = true;
      }
    }
  }, [connectedInstitutionId, wealthicaAccounts?.length]);

  // Auto-fetch positions when accounts are loaded (INITIAL LOAD ONLY)
  useEffect(() => {
    if (!autoFetchInitial) return; // rely on store-driven fetches by default

    const hasAccounts = wealthicaAccounts && wealthicaAccounts.length > 0;
    const institutionIds = Array.from(new Set((wealthicaAccounts || [])
      .map(acc => (acc as any).institutionId)
      .filter((id): id is string => !!id)));

    // Run auto-fetch only once to avoid cross-institution fetches during manual refreshes
    const shouldFetchPositions = hasAccounts && !isLoadingPositions && !didInitialPositionsLoadRef.current;

    if (shouldFetchPositions) {
      console.debug('🔥 POSITIONS AUTO-FETCH TRIGGERED (multi-institution, initial only)', {
        accountCount: wealthicaAccounts.length,
        institutionIds,
        timestamp: new Date().toISOString()
      });
      
      loadAllPositionsForAccounts().then(() => {
        didInitialPositionsLoadRef.current = true;
        console.debug('✅ POSITIONS AUTO-FETCH COMPLETED');
      }).catch((error) => {
        console.error('❌ POSITIONS AUTO-FETCH FAILED:', error);
      });
    }
    // Keep dependencies stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wealthicaAccounts?.length, autoFetchInitial]);

  // Toggle account expansion
  const toggleAccountExpansion = useCallback((account: any): void => {
    if (!account?.id) {
      logger.error('useWealthicaPositions.toggleAccountExpansion', 'Tried to toggle invalid account', account);
      return;
    }
    
    setExpandedAccounts(prev => {
      const isExpanded = prev.includes(account.id);
      const newExpanded = isExpanded 
        ? prev.filter(id => id !== account.id)
        : [...prev, account.id];
      
      logger.info('useWealthicaPositions.toggleAccountExpansion', 'Account expansion toggled', {
        accountId: account.id,
        accountName: account.name,
        wasExpanded: isExpanded,
        nowExpanded: !isExpanded,
        allExpandedAccounts: newExpanded
      });
      
      return newExpanded;
    });
  }, []);

  // List-based setter for single-root Accordion pattern
  const setExpandedAccountsList = useCallback((next: string[]): void => {
    const normalize = (arr: string[]) => Array.from(new Set((arr || []).filter(Boolean))).sort();
    setExpandedAccounts(prev => {
      const a = normalize(prev);
      const b = normalize(next || []);
      if (a.length === b.length && a.every((v, i) => v === b[i])) {
        return prev; // no change
      }
      logger.info('useWealthicaPositions.setExpandedAccountsList', 'Expanded accounts list changed', {
        from: a,
        to: b
      });
      return b;
    });
  }, []);

  return {
    accountsWithPositions,
    organizedPositionsMap,
    expandedAccounts,
    loadingPositionsFor,
    isLoadingPositions,
    loadAllPositionsForAccounts,
    loadPositionsForAccount,
    toggleAccountExpansion,
    setExpandedAccountsList,
    hasLoadedInitialPositions: didInitialPositionsLoadRef.current
  };
}
